import React, { useState } from 'react';
import {
  Paper,
  TextField,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Button,
  Box,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Chip,
  Tooltip,
  Collapse,
  Divider,
  alpha
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { nanoid } from 'nanoid';
import CellConditionBuilder from './CellConditionBuilder';
import CellActionBuilder from './CellActionBuilder';
import AppDatePicker from '../../common/AppDatePicker';
import { getDisplayColumnPrefix } from '../../../utils/periodUtils';
import { normalizeCategoryType } from '../../../utils/tagValueUtils';

const IngestionRuleRow = ({ 
  rule, 
  index, 
  tableSchema, 
  onUpdateRule, 
  onDeleteRule,
  ruleOrder
}) => {
  const [expanded, setExpanded] = useState(!rule.name);
  
  // Extract all columns from the table schema
  const getAllColumns = () => {
    const columns = [];

    // Add row identifier
    if (tableSchema.identifier?.name) {
      columns.push(tableSchema.identifier.name);
    }
    
    // Add attribute columns
    if (tableSchema.detailColumns) {
      tableSchema.detailColumns.forEach(col => {
        columns.push(col.name);
      });
    }
    
    // Add metadata columns
    if (tableSchema.attributeColumns) {
      tableSchema.attributeColumns.forEach(col => {
        columns.push(col.name);
      });
    }
    if (tableSchema.tableAttributeColumns) {
      tableSchema.tableAttributeColumns.forEach(col => {
        columns.push(col.name);
      });
    }
    
    // Add value columns (flattened with dot notation). Only a time-series table has
    // metrics, each under its period prefix, and the period is a column of its own.
    if (tableSchema.metricColumns && tableSchema.metricColumns.schema) {
      const periodPrefix = getDisplayColumnPrefix(tableSchema);
      Object.entries(tableSchema.metricColumns.schema).forEach(([key, value]) => {
        if (value.type) {
          columns.push(`${periodPrefix}.${key}`);
        } else {
          // Nested schema
          Object.keys(value).forEach((subKey) => {
            columns.push(`${periodPrefix}.${key}.${subKey}`);
          });
        }
      });
      columns.push(periodPrefix);
    }

    return columns;
  };

  const getColumnTypeMap = () => {
    const typeMap = {};

    // Normalised like every other column: the validator normalises before it decides
    // what a column supports, so an alias stored on a column ('boolean', 'float')
    // would otherwise let the editor offer conditions the validator then refuses.
    if (tableSchema?.identifier?.name) {
      typeMap[tableSchema.identifier.name] = normalizeCategoryType(tableSchema.identifier.type);
    }
    (tableSchema?.detailColumns || []).forEach((col) => {
      typeMap[col.name] = normalizeCategoryType(col.type);
    });
    (tableSchema?.attributeColumns || []).forEach((col) => {
      typeMap[col.name] = normalizeCategoryType(col.type);
    });
    (tableSchema?.tableAttributeColumns || []).forEach((col) => {
      typeMap[col.name] = normalizeCategoryType(col.type);
    });

    // A time-series table's metrics, under the period prefix, and its period.
    if (tableSchema?.metricColumns?.schema) {
      const periodPrefix = getDisplayColumnPrefix(tableSchema);
      Object.entries(tableSchema.metricColumns.schema).forEach(([key, value]) => {
        if (value.type) {
          typeMap[`${periodPrefix}.${key}`] = normalizeCategoryType(value.type);
        } else {
          Object.entries(value).forEach(([subKey, subValue]) => {
            typeMap[`${periodPrefix}.${key}.${subKey}`] = normalizeCategoryType(subValue.type);
          });
        }
      });
      typeMap[periodPrefix] = 'Date';
    }

    return typeMap;
  };

  const availableColumns = getAllColumns();
  const columnTypeMap = getColumnTypeMap();
  
  const operators = [
    // Existence operators
    { value: 'is_empty', label: 'Is Empty', category: 'existence', types: ['Text', 'Number', 'Date', 'Yes/No'] },
    { value: 'is_not_empty', label: 'Is Not Empty', category: 'existence', types: ['Text', 'Number', 'Date', 'Yes/No'] },
    
    // Comparison operators
    { value: 'equals', label: 'Equals', category: 'comparison', types: ['Text', 'Number', 'Date', 'Yes/No'], supportsColumnRhs: true },
    { value: 'not_equals', label: 'Not Equals', category: 'comparison', types: ['Text', 'Number', 'Date', 'Yes/No'], supportsColumnRhs: true },
    { value: 'contains', label: 'Contains', category: 'text', types: ['Text'] },
    { value: 'not_contains', label: 'Not Contains', category: 'text', types: ['Text'] },
    
    // Numeric operators
    { value: 'greater_than', label: 'Greater Than', category: 'numeric', types: ['Number', 'Date'], supportsColumnRhs: true },
    { value: 'less_than', label: 'Less Than', category: 'numeric', types: ['Number', 'Date'], supportsColumnRhs: true },
    { value: 'greater_than_or_equal', label: 'Greater or Equal', category: 'numeric', types: ['Number', 'Date'], supportsColumnRhs: true },
    { value: 'less_than_or_equal', label: 'Less or Equal', category: 'numeric', types: ['Number', 'Date'], supportsColumnRhs: true },
    { value: 'between', label: 'Between', category: 'numeric', types: ['Number', 'Date'], supportsColumnRhs: true },
    
    // Type operators
    { value: 'is_date', label: 'Is Valid Date', category: 'type', types: ['Text', 'Number', 'Date', 'Yes/No'] },
    { value: 'is_number', label: 'Is Number', category: 'type', types: ['Text', 'Number', 'Date', 'Yes/No'] },
    { value: 'is_text', label: 'Is Text', category: 'type', types: ['Text', 'Number', 'Date', 'Yes/No'] },
    { value: 'is_boolean', label: 'Is Boolean', category: 'type', types: ['Text', 'Number', 'Date', 'Yes/No'] },
    
    // String operators
    { value: 'length_equals', label: 'Length Equals', category: 'string', types: ['Text'] },
    { value: 'length_greater_than', label: 'Length Greater Than', category: 'string', types: ['Text'] },
    { value: 'length_less_than', label: 'Length Less Than', category: 'string', types: ['Text'] },
    { value: 'starts_with', label: 'Starts With', category: 'string', types: ['Text'] },
    { value: 'ends_with', label: 'Ends With', category: 'string', types: ['Text'] },
    { value: 'matches_regex', label: 'Matches Pattern', category: 'string', types: ['Text'] }
  ];

  const handleRuleChange = (field, value) => {
    if (field === 'level' && value !== rule.level) {
      // Row and cell rules have different targets and conditions. Keep only
      // the name and document identity; the new rule must be configured afresh.
      onUpdateRule(index, {
        id: rule.id, name: rule.name, level: value, enabled: true,
        conditions: [], condition_logic: 'AND', action: {},
      });
      setExpanded(true);
      return;
    }
    const updatedRule = { ...rule, [field]: value };
    onUpdateRule(index, updatedRule);
  };

  // Off clears the advanced conditions as well as the flag: while any remain, the advanced
  // editor stays open and the simple Cell Condition never comes back (M216).
  const handleAdvancedConditionsToggle = (checked) => {
    const updatedRule = checked
      ? { ...rule, use_advanced_conditions: true }
      : { ...rule, use_advanced_conditions: false, conditions: [] };
    onUpdateRule(index, updatedRule);
  };

  const updateCondition = (condIndex, updates) => {
    const updatedConditions = [...(rule.conditions || [])];
    const condition = { ...updatedConditions[condIndex], ...updates };
    if ('column' in updates || 'operator' in updates) {
      // Operands belong to the chosen column and operator. Clear every stored
      // operand representation so none can override the new editor's choices.
      Object.assign(condition, {
        compare_source: 'literal', compare_source2: 'literal',
        compare_column: '', compare_column2: '',
        compare_value: null, compare_value2: null,
        value: null, value2: null,
      });
      delete condition.rhs1;
      delete condition.rhs2;
    }
    updatedConditions[condIndex] = condition;
    const updatedRule = { ...rule, conditions: updatedConditions };
    onUpdateRule(index, updatedRule);
  };

  const handleAddCondition = () => {
    const newCondition = {
      id: nanoid(),
      column: '',
      operator: 'is_empty',
      compare_source: 'literal',
      compare_source2: 'literal',
      compare_value: null,
      compare_value2: null,
      compare_column: '',
      compare_column2: '',
      value: null,
      value2: null,
      negate: false
    };
    const updatedRule = { ...rule, conditions: [...(rule.conditions || []), newCondition] };
    onUpdateRule(index, updatedRule);
  };

  const handleDeleteCondition = (condIndex) => {
    const updatedConditions = (rule.conditions || []).filter((_, idx) => idx !== condIndex);
    const updatedRule = { ...rule, conditions: updatedConditions };
    onUpdateRule(index, updatedRule);
  };

  const handleCellConditionChange = (newCondition) => {
    const updatedRule = { ...rule, cell_condition: newCondition };
    onUpdateRule(index, updatedRule);
  };

  const handleCellActionChange = (newAction) => {
    const updatedRule = { ...rule, action: newAction };
    onUpdateRule(index, updatedRule);
  };

  const getValueFieldType = (operator) => {
    // No value needed
    if (['is_empty', 'is_not_empty', 'is_date', 'is_number', 'is_text', 'is_boolean'].includes(operator)) {
      return null;
    }
    
    // Two values needed (for between)
    if (operator === 'between') {
      return 'two_values';
    }
    
    // Pattern field for regex
    if (operator === 'matches_regex') {
      return 'pattern';
    }
    
    // Single value for everything else
    return 'single_value';
  };

  const getColumnType = (columnName) => columnTypeMap[columnName] || 'Text';

  const getAvailableOperatorsForColumn = (columnName) => {
    const columnType = getColumnType(columnName);
    return operators.filter((operator) => !columnName || (operator.types || []).includes(columnType));
  };

  const supportsColumnRhs = (operatorValue) => {
    const operator = operators.find((item) => item.value === operatorValue);
    return !!operator?.supportsColumnRhs;
  };

  const getCompatibleColumns = (columnName, operatorValue) => {
    const lhsType = getColumnType(columnName);
    if (!supportsColumnRhs(operatorValue)) {
      return [];
    }
    return availableColumns.filter((candidate) => {
      if (!candidate || candidate === columnName) return false;
      return getColumnType(candidate) === lhsType;
    });
  };

  const renderLiteralInput = (declaredType, label, value, onChange, inputProps) => {
    if (declaredType === 'Date') {
      return (
        <AppDatePicker
          fullWidth
          label={label}
          value={value}
          onChange={onChange}
        />
      );
    }

    if (declaredType === 'Number') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          inputProps={inputProps}
          label={label}
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }

    if (declaredType === 'Yes/No') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{label}</InputLabel>
          <Select
            value={value === true ? 'true' : value === false ? 'false' : ''}
            label={label}
            onChange={(e) => onChange(e.target.value === 'true')}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value === null || value === undefined ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  };

  const renderConditionValueField = (condition, condIndex, bound = 1) => {
    const operatorValue = condition.operator || 'is_empty';
    const fieldType = getValueFieldType(operatorValue);
    if (!fieldType) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          No value needed
        </Typography>
      );
    }

    const isSecondBound = bound === 2;
    const sourceField = isSecondBound ? 'compare_source2' : 'compare_source';
    const columnField = isSecondBound ? 'compare_column2' : 'compare_column';
    const literalField = isSecondBound ? 'compare_value2' : 'compare_value';
    const legacyField = isSecondBound ? 'value2' : 'value';
    const label = isSecondBound ? 'To' : 'Value';
    const declaredType = getColumnType(condition.column);
    const currentSource = condition[sourceField] || 'literal';
    const currentValue = condition[literalField] ?? condition[legacyField] ?? '';

    if (operatorValue.startsWith('length_')) {
      return renderLiteralInput('Number', 'Character count', currentValue, nextValue => updateCondition(condIndex, {
        [literalField]: nextValue,
        [legacyField]: nextValue,
      }), { min: 0, step: 1 });
    }

    if (operatorValue === 'matches_regex') {
      return (
        <Box display="flex" alignItems="center" gap={1}>
          <TextField
            fullWidth
            size="small"
            label="Pattern"
            value={currentValue}
            onChange={(e) => updateCondition(condIndex, { [literalField]: e.target.value, [legacyField]: e.target.value })}
            placeholder="e.g., ^[A-Z]{2}\\d{4}$"
          />
          <Tooltip title="Regular expression pattern. Example: ^[A-Z]{2}\\d{4}$ matches two uppercase letters followed by four digits.">
            <IconButton size="small" sx={{ p: 0.5 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      );
    }

    if (!supportsColumnRhs(operatorValue)) {
      return renderLiteralInput(declaredType, label, currentValue, (nextValue) => updateCondition(condIndex, {
        [literalField]: nextValue,
        [legacyField]: nextValue
      }));
    }

    const compatibleColumns = getCompatibleColumns(condition.column, operatorValue);

    return (
      <Box display="flex" gap={1}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Source</InputLabel>
          <Select
            value={currentSource}
            label="Source"
            onChange={(e) => updateCondition(condIndex, {
              [sourceField]: e.target.value,
              [columnField]: '',
            })}
          >
            <MenuItem value="literal">Fixed Value</MenuItem>
            <MenuItem value="column">Column</MenuItem>
          </Select>
        </FormControl>

        {currentSource === 'column' ? (
          <FormControl fullWidth size="small">
            <InputLabel>{label} Column</InputLabel>
            <Select
              value={condition[columnField] || ''}
              label={`${label} Column`}
              onChange={(e) => updateCondition(condIndex, { [columnField]: e.target.value })}
            >
              <MenuItem value="">
                <em>Select Column</em>
              </MenuItem>
              {compatibleColumns.map((column) => (
                <MenuItem key={column} value={column}>{column}</MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Box sx={{ flex: 1 }}>
            {renderLiteralInput(declaredType, label, currentValue, (nextValue) => updateCondition(condIndex, {
              [literalField]: nextValue,
              [legacyField]: nextValue
            }))}
          </Box>
        )}
      </Box>
    );
  };

  const categoryLabels = {
    existence: 'Existence Check',
    comparison: 'Comparison',
    numeric: 'Numeric',
    type: 'Data Type',
    string: 'String Operations',
    text: 'Text Search'
  };

  const isRowLevel = !rule.level || rule.level === 'row';

  return (
    <Paper 
      sx={{ 
        mb: 2,
        border: rule.enabled !== false ? '1px solid' : '1px dashed',
        borderColor: rule.enabled !== false ? 'divider' : alpha('#fff', 0.2),
        backgroundColor: rule.enabled !== false ? 'background.paper' : alpha('#000', 0.02),
        transition: 'all 0.2s ease',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(78, 133, 235, 0.15)'
        }
      }}
    >
      {/* Header */}
      <Box 
        sx={{ 
          p: 2,
          backgroundColor: rule.enabled !== false ? alpha('#4e85eb', 0.08) : 'transparent',
          borderBottom: '1px solid',
          borderColor: 'divider',
          cursor: 'pointer'
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs>
            <Box display="flex" alignItems="center" gap={2}>
              <Box
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                sx={{ 
                  p: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'grab',
                  color: 'text.secondary',
                  '&:hover': { color: 'primary.main' },
                  '&:active': { cursor: 'grabbing' }
                }}
              >
                <DragIndicatorIcon fontSize="small" />
              </Box>
              <Chip 
                label={`#${ruleOrder}`} 
                size="small" 
                color="default"
                sx={{ 
                  minWidth: 40,
                  fontWeight: 600,
                  backgroundColor: alpha('#4e85eb', 0.1),
                  color: '#4e85eb'
                }}
              />
              <IconButton size="small" sx={{ p: 0 }}>
                {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
              <TextField
                size="small"
                variant="standard"
                value={rule.name || ''}
                onChange={(e) => {
                  e.stopPropagation();
                  handleRuleChange('name', e.target.value);
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Enter rule name..."
                sx={{ 
                  flex: 1,
                  '& .MuiInput-underline:before': { borderBottom: 'none' },
                  '& .MuiInput-underline:hover:before': { borderBottom: '1px solid rgba(255,255,255,0.42)' },
                  '& .MuiInput-underline:after': { borderBottom: '2px solid #4e85eb' }
                }}
                InputProps={{
                  sx: { fontSize: '1.1rem', fontWeight: 500 }
                }}
              />
              <Chip 
                label={isRowLevel ? 'Row Level' : 'Cell Level'} 
                size="small" 
                color={isRowLevel ? 'primary' : 'secondary'}
                sx={{ ml: 1 }}
              />
            </Box>
          </Grid>
          <Grid item>
            <Box display="flex" alignItems="center" gap={2}>
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={rule.level || 'row'}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleRuleChange('level', e.target.value);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ height: 32 }}
                >
                  <MenuItem value="row">Row</MenuItem>
                  <MenuItem value="cell">Cell</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={rule.enabled !== false}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleRuleChange('enabled', e.target.checked);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    size="small"
                  />
                }
                label={rule.enabled !== false ? 'Enabled' : 'Disabled'}
                sx={{ m: 0 }}
              />
              <Tooltip title="Delete Rule">
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteRule(index);
                  }}
                  sx={{ 
                    color: 'error.main',
                    '&:hover': { backgroundColor: alpha('#e84a5f', 0.1) }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Content */}
      <Collapse in={expanded}>
        <Box sx={{ p: 3 }}>
          {isRowLevel ? (
            <>
              {/* Row Level Rule Content (existing code) */}
              {/* Conditions Section */}
              <Box sx={{ mb: 4 }}>
                <Box display="flex" alignItems="center" gap={1} mb={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Conditions
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={rule.condition_logic || 'AND'}
                      onChange={(e) => handleRuleChange('condition_logic', e.target.value)}
                      sx={{ 
                        height: 32,
                        '& .MuiSelect-select': { py: 0.5 }
                      }}
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    logic between conditions
                  </Typography>
                </Box>

                {(rule.conditions || []).map((condition, condIndex) => (
                  <Paper
                    role="group"
                    aria-label={`Condition ${condIndex + 1}`}
                    key={condition.id}
                    variant="outlined"
                    sx={{ 
                      p: 2,
                      mb: 1,
                      backgroundColor: alpha('#4e85eb', 0.02),
                      position: 'relative'
                    }}
                  >
                    <Grid container spacing={2} alignItems="flex-start">
                      {/* Column Selection */}
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Column</InputLabel>
                          <Select
                            value={condition.column || ''}
                            onChange={(e) => updateCondition(condIndex, {
                              column: e.target.value,
                              operator: 'is_empty',
                            })}
                            label="Column"
                          >
                            <MenuItem value="">
                              <em>Select Column</em>
                            </MenuItem>
                            {availableColumns.map(col => (
                              <MenuItem key={col} value={col}>
                                <Typography variant="body2">{col}</Typography>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Operator Selection */}
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Operator</InputLabel>
                          <Select
                            value={condition.operator || 'is_empty'}
                            onChange={(e) => updateCondition(condIndex, {
                              operator: e.target.value,
                            })}
                            label="Operator"
                            renderValue={(value) => {
                              const op = operators.find(o => o.value === value);
                              return op ? op.label : value;
                            }}
                          >
                            <MenuItem value="" disabled>
                              <em>Select Operator</em>
                            </MenuItem>
                            {Object.entries(
                              getAvailableOperatorsForColumn(condition.column).reduce((grouped, op) => {
                                if (!grouped[op.category]) grouped[op.category] = [];
                                grouped[op.category].push(op);
                                return grouped;
                              }, {})
                            ).map(([category, ops]) => [
                              <MenuItem 
                                key={`cat-${category}`} 
                                disabled
                                sx={{ 
                                  backgroundColor: alpha('#4e85eb', 0.05),
                                  py: 0.5,
                                  borderTop: '1px solid',
                                  borderColor: 'divider'
                                }}
                              >
                                <Typography variant="caption" fontWeight={600}>
                                  {categoryLabels[category] || category.toUpperCase()}
                                </Typography>
                              </MenuItem>,
                              ...ops.map(op => (
                                <MenuItem 
                                  key={op.value} 
                                  value={op.value} 
                                  sx={{ pl: 4 }}
                                >
                                  {op.label}
                                </MenuItem>
                              ))
                            ])}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Value Field(s) */}
                      <Grid item xs={12} md={4}>
                        {(() => {
                          const fieldType = getValueFieldType(condition.operator);
                          
                          if (fieldType === 'single_value') {
                            return renderConditionValueField(condition, condIndex, 1);
                          } else if (fieldType === 'two_values') {
                            return (
                              <Box display="flex" gap={1}>
                                <Box sx={{ flex: 1 }}>{renderConditionValueField(condition, condIndex, 1)}</Box>
                                <Box sx={{ flex: 1 }}>{renderConditionValueField(condition, condIndex, 2)}</Box>
                              </Box>
                            );
                          } else if (fieldType === 'pattern') {
                            return renderConditionValueField(condition, condIndex, 1);
                          }
                          return (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              No value needed
                            </Typography>
                          );
                        })()}
                      </Grid>

                      {/* NOT checkbox and Delete */}
                      <Grid item xs={12} md={2}>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={condition.negate || false}
                                onChange={(e) => updateCondition(condIndex, { negate: e.target.checked })}
                                size="small"
                              />
                            }
                            label={
                              <Typography variant="body2" color={condition.negate ? 'error.main' : 'text.secondary'}>
                                NOT
                              </Typography>
                            }
                            sx={{ m: 0 }}
                          />
                          <IconButton 
                            size="small" 
                            onClick={() => handleDeleteCondition(condIndex)}
                            disabled={rule.conditions.length === 1}
                            sx={{ 
                              color: 'error.main',
                              '&:hover': { backgroundColor: alpha('#e84a5f', 0.1) },
                              '&.Mui-disabled': { color: 'text.disabled' }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>
                ))}

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddCondition}
                  sx={{ 
                    mt: 1,
                    borderStyle: 'dashed',
                    '&:hover': { borderStyle: 'solid' }
                  }}
                >
                  Add Condition
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Action Section */}
              <Box>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Action
                </Typography>
                <CellActionBuilder
                  action={rule.action || { type: 'skip_row' }}
                  onUpdateAction={handleCellActionChange}
                  availableColumns={availableColumns}
                  targetColumn={rule.action?.column}
                  columnTypeMap={columnTypeMap}
                  allowTargetColumnSelection
                />
              </Box>
            </>
          ) : (
            <>
              {/* Cell Level Rule Content */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Target Column
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Column</InputLabel>
                  <Select
                    value={rule.target_column || ''}
                    onChange={(e) => handleRuleChange('target_column', e.target.value)}
                    label="Column"
                  >
                    <MenuItem value="">
                      <em>Select Column</em>
                    </MenuItem>
                    {availableColumns.map(col => (
                      <MenuItem key={col} value={col}>
                        <Typography variant="body2">{col}</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControlLabel
                  sx={{ mt: 2 }}
                  control={
                    <Switch
                      size="small"
                      checked={!!(rule.use_advanced_conditions || (rule.conditions && rule.conditions.length > 0))}
                      onChange={(e) => handleAdvancedConditionsToggle(e.target.checked)}
                    />
                  }
                  label="Use advanced conditions"
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              { (rule.use_advanced_conditions || (rule.conditions && rule.conditions.length > 0)) ? (
                <>
                  {/* Advanced Conditions (same as row-level) */}
                  <Box sx={{ mb: 4 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        Conditions
                      </Typography>
                      <FormControl size="small" sx={{ minWidth: 100 }}>
                        <Select
                          value={rule.condition_logic || 'AND'}
                          onChange={(e) => handleRuleChange('condition_logic', e.target.value)}
                          sx={{ 
                            height: 32,
                            '& .MuiSelect-select': { py: 0.5 }
                          }}
                        >
                          <MenuItem value="AND">AND</MenuItem>
                          <MenuItem value="OR">OR</MenuItem>
                        </Select>
                      </FormControl>
                      <Typography variant="body2" color="text.secondary">
                        logic between conditions
                      </Typography>
                    </Box>

                    {(rule.conditions || []).map((condition, condIndex) => (
                      <Paper
                        role="group"
                        aria-label={`Condition ${condIndex + 1}`}
                        key={condition.id || condIndex}
                        variant="outlined"
                        sx={{ 
                          p: 2,
                          mb: 1,
                          backgroundColor: alpha('#4e85eb', 0.02),
                          position: 'relative'
                        }}
                      >
                        <Grid container spacing={2} alignItems="flex-start">
                          {/* Column Selection */}
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Column</InputLabel>
                              <Select
                                value={condition.column || ''}
                                onChange={(e) => updateCondition(condIndex, {
                                  column: e.target.value,
                                  operator: 'is_empty',
                                })}
                                label="Column"
                              >
                                <MenuItem value="">
                                  <em>Select Column</em>
                                </MenuItem>
                                {availableColumns.map(col => (
                                  <MenuItem key={col} value={col}>
                                    <Typography variant="body2">{col}</Typography>
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Operator Selection */}
                          <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Operator</InputLabel>
                              <Select
                                value={condition.operator || 'is_empty'}
                                onChange={(e) => updateCondition(condIndex, {
                                  operator: e.target.value,
                                })}
                                label="Operator"
                                renderValue={(value) => {
                                  const op = operators.find(o => o.value === value);
                                  return op ? op.label : value;
                                }}
                              >
                                <MenuItem value="" disabled>
                                  <em>Select Operator</em>
                                </MenuItem>
                                {Object.entries(
                                  getAvailableOperatorsForColumn(condition.column).reduce((grouped, op) => {
                                    if (!grouped[op.category]) grouped[op.category] = [];
                                    grouped[op.category].push(op);
                                    return grouped;
                                  }, {})
                                ).map(([category, ops]) => [
                                  <MenuItem 
                                    key={`cat-${category}`} 
                                    disabled
                                    sx={{ 
                                      backgroundColor: alpha('#4e85eb', 0.05),
                                      py: 0.5,
                                      borderTop: '1px solid',
                                      borderColor: 'divider'
                                    }}
                                  >
                                    <Typography variant="caption" fontWeight={600}>
                                      {categoryLabels[category] || category.toUpperCase()}
                                    </Typography>
                                  </MenuItem>,
                                  ...ops.map(op => (
                                    <MenuItem 
                                      key={op.value} 
                                      value={op.value} 
                                      sx={{ pl: 4 }}
                                    >
                                      {op.label}
                                    </MenuItem>
                                  ))
                                ])}
                              </Select>
                            </FormControl>
                          </Grid>

                          {/* Value Field(s) */}
                          <Grid item xs={12} md={4}>
                            {(() => {
                              const fieldType = getValueFieldType(condition.operator);
                              if (fieldType === 'single_value') {
                                return renderConditionValueField(condition, condIndex, 1);
                              } else if (fieldType === 'two_values') {
                                return (
                                  <Box display="flex" gap={1}>
                                    <Box sx={{ flex: 1 }}>{renderConditionValueField(condition, condIndex, 1)}</Box>
                                    <Box sx={{ flex: 1 }}>{renderConditionValueField(condition, condIndex, 2)}</Box>
                                  </Box>
                                );
                              } else if (fieldType === 'pattern') {
                                return renderConditionValueField(condition, condIndex, 1);
                              }
                              return (
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                  No value needed
                                </Typography>
                              );
                            })()}
                          </Grid>

                          {/* NOT checkbox and Delete */}
                          <Grid item xs={12} md={2}>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={condition.negate || false}
                                    onChange={(e) => updateCondition(condIndex, { negate: e.target.checked })}
                                    size="small"
                                  />
                                }
                                label={
                                  <Typography variant="body2" color={condition.negate ? 'error.main' : 'text.secondary'}>
                                    NOT
                                  </Typography>
                                }
                                sx={{ m: 0 }}
                              />
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteCondition(condIndex)}
                                disabled={(rule.conditions || []).length === 1}
                                sx={{ 
                                  color: 'error.main',
                                  '&:hover': { backgroundColor: alpha('#e84a5f', 0.1) },
                                  '&.Mui-disabled': { color: 'text.disabled' }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </Grid>
                        </Grid>
                      </Paper>
                    ))}

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleAddCondition}
                      sx={{ 
                        mt: 1,
                        borderStyle: 'dashed',
                        '&:hover': { borderStyle: 'solid' }
                      }}
                    >
                      Add Condition
                    </Button>
                  </Box>

                  <Divider sx={{ my: 3 }} />
                </>
              ) : (
                <>
                  <Box sx={{ mb: 4 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                      Cell Condition
                    </Typography>
                    <CellConditionBuilder
                      condition={rule.cell_condition || { type: 'always' }}
                      onUpdateCondition={handleCellConditionChange}
                      targetType={getColumnType(rule.target_column)}
                    />
                  </Box>

                  <Divider sx={{ my: 3 }} />
                </>
              )}

              <Box>
                <Typography variant="subtitle1" fontWeight={600} mb={2}>
                  Action
                </Typography>
                <CellActionBuilder
                  action={rule.action || { type: 'skip_row' }}
                  onUpdateAction={handleCellActionChange}
                  availableColumns={availableColumns}
                  targetColumn={rule.target_column}
                  columnTypeMap={columnTypeMap}
                />
              </Box>
            </>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default IngestionRuleRow;
